import { ChangeDetectionStrategy, Component, ElementRef, Injector, OnInit, ViewChild } from '@angular/core';
import { Validators } from '@angular/forms';
import { MatDatepicker } from '@angular/material/datepicker';
import { MatDialogRef } from '@angular/material/dialog';
import { BehaviorSubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FirebaseService, UploadTypeEnum } from '../../../../public-api';
import { AbstractComponent } from '../../../commons/abstract.component';
import { UtilHelper } from '../../../helpers/util.helper';
import { LanguageService } from '../../../i18n/language.service';
import { LOCALE_LABELS, SUPPORTED_LOCALES, SupportedLocale } from '../../../i18n/supported-locale';
import { User } from '../../../models/user.model';
import { UserService } from '../../../services/user.service';

@Component({
    selector: 'lib-edit-basic-info-user',
    templateUrl: './edit-basic-info-user.component.html',
    styleUrls: ['./edit-basic-info-user.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class EditBasicInfoUserComponent extends AbstractComponent implements OnInit {
  @ViewChild('picker', { static: true }) picker: MatDatepicker<Date>;
  @ViewChild('fileInputFile', { static: false }) inputfile: ElementRef;

  private loadingSpinnerSubject = new BehaviorSubject<boolean>(false);
  loadingSpinner$ = this.loadingSpinnerSubject.asObservable();

  public userId: string;
  public user = {} as User;

  public file: File;
  public bytes: string;

  readonly locales: ReadonlyArray<SupportedLocale> = SUPPORTED_LOCALES;
  readonly localeLabels: Record<SupportedLocale, string> = LOCALE_LABELS;

  constructor(
    injector: Injector,
    public userService: UserService,
    public firebaseService: FirebaseService,
    public languageService: LanguageService,
    public dialogRef: MatDialogRef<EditBasicInfoUserComponent>
  ) {
    super(injector);
    this.setupForm();
  }

  get currentLanguage(): SupportedLocale {
    return this.languageService.currentLang;
  }

  // Personal language preference (RF-02, RF-12). Switches the UI live and
  // persists via PUT /api/user/me/language — independent of the profile Save.
  onLanguageChange(language: SupportedLocale): void {
    this.languageService.setLanguage(language);
  }

  ngOnInit(): void {
    this.userId = this.getIDCurrentUser();
    if (this.userId) {
      this.userService.getById(this.userId).pipe(takeUntil(this.destroy$))
        .subscribe((user: User) => {
          this.user = user;

          if (this.user) {
            this.formGroup.patchValue({ ...this.user }, { emitEvent: false });
          }
        }
        );
    }

  }

  private setupForm(): void {
    this.formGroup = this.formBuilder.group({
      name: [null, [Validators.required, Validators.minLength(3), Validators.maxLength(255)]],
      email: [null, [Validators.required, Validators.pattern(this.emailRegex)]],
      phone: [null, [Validators.required, Validators.pattern('^[0-9]+$')]],
      tin: [null, [Validators.pattern('^[0-9]+$')]],
      birthDate: [null],
      image: [null]
    });

    this.formGroup.valueChanges.subscribe((value) => {
      Object.assign(this.user, value);
    });
  }

  addImage() {
    this.inputfile.nativeElement.click();
  }

  userImage() {
    const imageUser = this.user.image;

    if (imageUser) {
      return imageUser;
    } else {
      return UtilHelper.no_image;
    }
  }


  onSelectImagem(event) {
    const target = event.target;
    const files: FileList = target.files;

    if (files[0].size > 1000000) {
      this.alertService.warning(this.t('shared.editBasicInfoUser.imageTooLarge'));
      return;
    }

    if (!(files[0].type === 'image/jpeg' || files[0].type === 'image/png')) {
      this.alertService.warning(this.t('shared.editBasicInfoUser.imageInvalidFormat'));
      return;
    }

    this.file = files[0];

    let reader = new FileReader();
    reader.onload = async (file) => {
      if (this.file) {
        this.loadingSpinnerSubject.next(true);
        const dateMilisecond = new Date().getTime();
        const filename: string = dateMilisecond.toString();

        this.firebaseService.uploadFile(this.file, filename, UploadTypeEnum.USER).then((snapshot: { url: string }) => {
          this.loadingSpinnerSubject.next(false);
          this.user.image = snapshot.url;
          this.formGroup.patchValue({ image: snapshot.url });
        })
          .catch((error) => this.alertService.error(this.t('shared.editBasicInfoUser.uploadError')));
      }
    };
    reader.readAsArrayBuffer(this.file);
  }

  removeMidia(): void {
    if (this.user.image) {
      this.firebaseService.deleteFile(this.user.image)
        .then(() => {
          this.resetFileState();
        })
        .catch(error => {
          this.alertService.error(this.t('shared.editBasicInfoUser.deleteError'));
          this.resetFileState();
        });
    }
  }

  private resetFileState(): void {
    this.user.image = '';
  }

  save(): void {
    this.loading = true;
    if (!this.formGroup.valid) {
      this.validateAllFormFields(this.formGroup);
      return;
    }
    this.userService.update(this.userId, this.user).subscribe({
      next: (_value: User) => {
        this.alertService.success(this.t('shared.editBasicInfoUser.updateSuccess'));
        this
      },
      error: (err) => { this.alertService.error(err.error.message); this.loading = false; },
      complete: () => { this.close(); this.loading = false; }
    });
  }

  close(): void {
    this.dialogRef.close();
  }
}
